import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ArrowLeft, X, ZoomIn, ZoomOut, MapPin, Square, Circle, MessageSquare, Trash2, Edit2, User, AlertCircle } from 'lucide-react';
import { API_BASE_URL } from '../../services/api';

interface ProjectFile {
  id: number;
  filename: string;
  original_name: string;
  file_url: string;
  file_type: string;
  file_size: number;
  image_width?: number;
  image_height?: number;
  uploaded_at: string;
}

interface Annotation {
  id: number;
  file_id: number;
  x_coordinate: number;
  y_coordinate: number;
  width: number;
  height: number;
  annotation_type: 'point' | 'rectangle' | 'circle';
  title: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed' | 'rejected';
  priority: 'low' | 'medium' | 'high' | 'critical';
  assigned_to: number | null;
  creator_first_name: string;
  creator_last_name: string;
  assignee_first_name?: string;
  assignee_last_name?: string;
  created_at: string;
}

interface TeamMember {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

interface ImageAnnotatorCanvasProps {
  files: ProjectFile[];
  taskId: number;
  projectId: number;
  users: TeamMember[];
  onAnnotationUpdate?: () => void;
}

type AnnotationTool = 'point' | 'rectangle' | 'circle';

export const ImageAnnotatorCanvas: React.FC<ImageAnnotatorCanvasProps> = ({
  files,
  taskId,
  projectId,
  users,
  onAnnotationUpdate
}) => {
  const [selectedFile, setSelectedFile] = useState<ProjectFile | null>(null);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [selectedAnnotation, setSelectedAnnotation] = useState<number | null>(null);
  const [commentInput, setCommentInput] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');
  const [scale, setScale] = useState(1);
  const [mode, setMode] = useState<AnnotationTool>('point');
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null);
  const [currentShape, setCurrentShape] = useState<any>(null);
  const [showAnnotator, setShowAnnotator] = useState(false);
  const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
  const [status, setStatus] = useState<'pending' | 'in_progress' | 'completed' | 'rejected'>('pending');
  const [assignedTo, setAssignedTo] = useState<number | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  // Load annotations for selected file
  const loadAnnotations = useCallback(async (fileId: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/annotations/file/${fileId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAnnotations(data.annotations);
      }
    } catch (error) {
      console.error('Failed to load annotations:', error);
    }
  }, []);

  useEffect(() => {
    if (selectedFile && showAnnotator) {
      loadAnnotations(selectedFile.id);
    }
  }, [selectedFile, showAnnotator, loadAnnotations]);

  const drawCanvas = (tempShape: any = null) => {
    const canvas = canvasRef.current;
    if (!canvas || !imageRef.current) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = imageRef.current;

    canvas.width = img.naturalWidth * scale;
    canvas.height = img.naturalHeight * scale;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const currentFileAnnotations = annotations.filter(a => a.file_id === selectedFile?.id);

    currentFileAnnotations.forEach((anno, idx) => {
      const isSelected = selectedAnnotation === anno.id;

      if (anno.annotation_type === 'point') {
        const x = anno.x_coordinate * scale;
        const y = anno.y_coordinate * scale;

        ctx.beginPath();
        ctx.arc(x, y, 6, 0, 2 * Math.PI);
        ctx.fillStyle = isSelected ? '#2563eb' : '#ef4444';
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.stroke();

        ctx.font = 'bold 14px sans-serif';
        ctx.fillStyle = '#1e293b';
        ctx.fillText(`#${idx + 1}`, x + 10, y - 10);
      } else if (anno.annotation_type === 'rectangle') {
        const x = anno.x_coordinate * scale;
        const y = anno.y_coordinate * scale;
        const w = anno.width * scale;
        const h = anno.height * scale;

        ctx.strokeStyle = isSelected ? '#2563eb' : '#ef4444';
        ctx.lineWidth = 3;
        ctx.strokeRect(x, y, w, h);
        ctx.fillStyle = isSelected ? 'rgba(37, 99, 235, 0.1)' : 'rgba(239, 68, 68, 0.1)';
        ctx.fillRect(x, y, w, h);

        ctx.font = 'bold 14px sans-serif';
        ctx.fillStyle = '#1e293b';
        ctx.fillText(`#${idx + 1}`, x + 5, y - 5);
      } else if (anno.annotation_type === 'circle') {
        const cx = anno.x_coordinate * scale;
        const cy = anno.y_coordinate * scale;
        const r = (anno.width / 2) * scale; // Use width as diameter

        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, 2 * Math.PI);
        ctx.strokeStyle = isSelected ? '#2563eb' : '#ef4444';
        ctx.lineWidth = 3;
        ctx.stroke();
        ctx.fillStyle = isSelected ? 'rgba(37, 99, 235, 0.1)' : 'rgba(239, 68, 68, 0.1)';
        ctx.fill();

        ctx.font = 'bold 14px sans-serif';
        ctx.fillStyle = '#1e293b';
        ctx.fillText(`#${idx + 1}`, cx + r + 5, cy);
      }
    });

    if (tempShape) {
      ctx.strokeStyle = '#2563eb';
      ctx.lineWidth = 3;
      ctx.setLineDash([5, 5]);

      if (tempShape.type === 'rectangle') {
        ctx.strokeRect(tempShape.x * scale, tempShape.y * scale, tempShape.width * scale, tempShape.height * scale);
        ctx.fillStyle = 'rgba(37, 99, 235, 0.1)';
        ctx.fillRect(tempShape.x * scale, tempShape.y * scale, tempShape.width * scale, tempShape.height * scale);
      } else if (tempShape.type === 'circle') {
        ctx.beginPath();
        ctx.arc(tempShape.cx * scale, tempShape.cy * scale, tempShape.radius * scale, 0, 2 * Math.PI);
        ctx.stroke();
        ctx.fillStyle = 'rgba(37, 99, 235, 0.1)';
        ctx.fill();
      }

      ctx.setLineDash([]);
    }
  };

  useEffect(() => {
    if (selectedFile && showAnnotator) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = selectedFile.file_url;
      img.onload = () => {
        imageRef.current = img;
        drawCanvas();
      };
    }
  }, [selectedFile, showAnnotator]);

  useEffect(() => {
    drawCanvas(currentShape);
  }, [annotations, scale, selectedAnnotation, currentShape]);

  const getCanvasCoords = (e: React.MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    return {
      x: Math.round((e.clientX - rect.left) / scale),
      y: Math.round((e.clientY - rect.top) / scale)
    };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const coords = getCanvasCoords(e);
    const currentFileAnnotations = annotations.filter(a => a.file_id === selectedFile?.id);

    const clickedAnno = currentFileAnnotations.find(anno => {
      if (anno.annotation_type === 'point') {
        const dx = anno.x_coordinate - coords.x;
        const dy = anno.y_coordinate - coords.y;
        return Math.sqrt(dx * dx + dy * dy) < 10 / scale;
      } else if (anno.annotation_type === 'rectangle') {
        return coords.x >= anno.x_coordinate && coords.x <= anno.x_coordinate + anno.width &&
               coords.y >= anno.y_coordinate && coords.y <= anno.y_coordinate + anno.height;
      } else if (anno.annotation_type === 'circle') {
        const dx = anno.x_coordinate - coords.x;
        const dy = anno.y_coordinate - coords.y;
        const radius = anno.width / 2;
        return Math.sqrt(dx * dx + dy * dy) <= radius;
      }
      return false;
    });

    if (clickedAnno) {
      setSelectedAnnotation(clickedAnno.id);
      setCommentInput(clickedAnno.title);
      setDescriptionInput(clickedAnno.description || '');
      setPriority(clickedAnno.priority);
      setStatus(clickedAnno.status);
      setAssignedTo(clickedAnno.assigned_to);
      return;
    }

    if (mode === 'point') {
      // For point mode, open form immediately
      setDrawStart(coords);
      setCurrentShape({ type: 'point', x: coords.x, y: coords.y });
      setSelectedAnnotation(null);
      setCommentInput('');
      setDescriptionInput('');
      setPriority('medium');
      setStatus('pending');
      setAssignedTo(null);
    } else {
      setIsDrawing(true);
      setDrawStart(coords);
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDrawing || !drawStart) return;

    const coords = getCanvasCoords(e);

    if (mode === 'rectangle') {
      const x = Math.min(drawStart.x, coords.x);
      const y = Math.min(drawStart.y, coords.y);
      const width = Math.abs(coords.x - drawStart.x);
      const height = Math.abs(coords.y - drawStart.y);

      setCurrentShape({ type: 'rectangle', x, y, width, height });
    } else if (mode === 'circle') {
      const dx = coords.x - drawStart.x;
      const dy = coords.y - drawStart.y;
      const radius = Math.sqrt(dx * dx + dy * dy);

      setCurrentShape({ type: 'circle', cx: drawStart.x, cy: drawStart.y, radius });
    }
  };

  const handleMouseUp = () => {
    if (!isDrawing || !currentShape) {
      // Handle point annotation
      if (mode === 'point' && currentShape && currentShape.type === 'point') {
        // Point is ready, show form
        return;
      }
      return;
    }

    if ((currentShape.type === 'rectangle' && currentShape.width > 5 && currentShape.height > 5) ||
        (currentShape.type === 'circle' && currentShape.radius > 5)) {
      // Shape is ready, show form
      setIsDrawing(false);
      // Keep currentShape for saving
    } else {
      // Shape too small, cancel
      setIsDrawing(false);
      setDrawStart(null);
      setCurrentShape(null);
    }
  };

  const handleSaveAnnotation = async () => {
    if (!commentInput.trim() || !currentShape || !selectedFile) return;

    try {
      const token = localStorage.getItem('token');

      let annotationData: any = {
        file_id: selectedFile.id,
        task_id: taskId,
        project_id: projectId,
        annotation_type: currentShape.type,
        title: commentInput.trim(),
        description: descriptionInput.trim(),
        priority,
        assigned_to: assignedTo
      };

      if (currentShape.type === 'point') {
        annotationData.x_coordinate = Math.round(currentShape.x);
        annotationData.y_coordinate = Math.round(currentShape.y);
        annotationData.width = 0;
        annotationData.height = 0;
      } else if (currentShape.type === 'rectangle') {
        annotationData.x_coordinate = Math.round(currentShape.x);
        annotationData.y_coordinate = Math.round(currentShape.y);
        annotationData.width = Math.round(currentShape.width);
        annotationData.height = Math.round(currentShape.height);
      } else if (currentShape.type === 'circle') {
        annotationData.x_coordinate = Math.round(currentShape.cx);
        annotationData.y_coordinate = Math.round(currentShape.cy);
        annotationData.width = Math.round(currentShape.radius * 2); // Store diameter as width
        annotationData.height = Math.round(currentShape.radius * 2);
      }

      const response = await fetch(`${API_BASE_URL}/api/annotations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(annotationData)
      });

      if (response.ok) {
        await loadAnnotations(selectedFile.id);
        setCommentInput('');
        setDescriptionInput('');
        setCurrentShape(null);
        setDrawStart(null);
        setPriority('medium');
        setStatus('pending');
        setAssignedTo(null);
        onAnnotationUpdate?.();
      } else {
        const errorData = await response.json();
        console.error('Failed to save annotation:', errorData);
        alert(`Failed to save annotation: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to create annotation:', error);
      alert('Failed to create annotation. Please try again.');
    }
  };

  const handleUpdateAnnotation = async () => {
    if (!selectedAnnotation || !commentInput.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/annotations/${selectedAnnotation}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: commentInput.trim(),
          description: descriptionInput.trim(),
          priority,
          status,
          assigned_to: assignedTo
        })
      });

      if (response.ok && selectedFile) {
        await loadAnnotations(selectedFile.id);
        setSelectedAnnotation(null);
        setCommentInput('');
        setDescriptionInput('');
        setPriority('medium');
        setStatus('pending');
        setAssignedTo(null);
        onAnnotationUpdate?.();
      }
    } catch (error) {
      console.error('Failed to update annotation:', error);
    }
  };

  const handleDeleteAnnotation = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this annotation?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/annotations/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok && selectedFile) {
        await loadAnnotations(selectedFile.id);
        if (selectedAnnotation === id) {
          setSelectedAnnotation(null);
          setCommentInput('');
          setDescriptionInput('');
        }
        onAnnotationUpdate?.();
      }
    } catch (error) {
      console.error('Failed to delete annotation:', error);
    }
  };

  const openAnnotator = (file: ProjectFile) => {
    setSelectedFile(file);
    setShowAnnotator(true);
    setScale(1);
    setMode('point');
    setSelectedAnnotation(null);
    setCommentInput('');
    setDescriptionInput('');
  };

  const getAnnotationDescription = (anno: Annotation) => {
    if (anno.annotation_type === 'point') {
      return `x: ${anno.x_coordinate}px, y: ${anno.y_coordinate}px`;
    } else if (anno.annotation_type === 'rectangle') {
      return `${anno.width}×${anno.height}px at (${anno.x_coordinate}, ${anno.y_coordinate})`;
    } else if (anno.annotation_type === 'circle') {
      const radius = Math.round(anno.width / 2);
      return `radius: ${radius}px at (${anno.x_coordinate}, ${anno.y_coordinate})`;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50';
      case 'in_progress': return 'text-blue-600 bg-blue-50';
      case 'rejected': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const currentFileAnnotations = selectedFile
    ? annotations.filter(a => a.file_id === selectedFile.id)
    : [];

  if (showAnnotator && selectedFile) {
    return (
      <div className="fixed inset-0 bg-gray-900 z-50 flex flex-col">
        {/* Annotator Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{selectedFile.original_name}</h2>
                <p className="text-sm text-gray-500">Annotating image</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setMode('point')}
                  className={`p-2 rounded transition-colors ${
                    mode === 'point' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-200'
                  }`}
                  title="Point Tool"
                >
                  <MapPin className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setMode('rectangle')}
                  className={`p-2 rounded transition-colors ${
                    mode === 'rectangle' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-200'
                  }`}
                  title="Rectangle Tool"
                >
                  <Square className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setMode('circle')}
                  className={`p-2 rounded transition-colors ${
                    mode === 'circle' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-200'
                  }`}
                  title="Circle Tool"
                >
                  <Circle className="w-4 h-4" />
                </button>
              </div>
              <div className="w-px h-6 bg-gray-300"></div>
              <button
                onClick={() => setScale(prev => Math.min(prev + 0.2, 3))}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
              <button
                onClick={() => setScale(prev => Math.max(prev - 0.2, 0.5))}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="px-3 py-2 bg-gray-100 rounded-lg text-sm font-medium">
                {Math.round(scale * 100)}%
              </span>
              <button
                onClick={() => {
                  setShowAnnotator(false);
                  setSelectedAnnotation(null);
                  setCommentInput('');
                  setDescriptionInput('');
                  setCurrentShape(null);
                }}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors ml-2"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Annotator Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Canvas Area */}
          <div className="flex-1 bg-gray-800 overflow-auto p-6 flex items-center justify-center">
            <canvas
              ref={canvasRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              className="cursor-crosshair bg-white shadow-2xl"
            />
          </div>

          {/* Annotations Sidebar */}
          <div className="w-96 bg-white border-l border-gray-200 overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Annotations ({currentFileAnnotations.length})
              </h3>

              {(selectedAnnotation || currentShape) && (
                <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-3">
                    {selectedAnnotation ? 'Edit Annotation' : 'Add Annotation'}
                  </h4>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Title *
                      </label>
                      <input
                        type="text"
                        value={commentInput}
                        onChange={(e) => setCommentInput(e.target.value)}
                        placeholder="Enter annotation title..."
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={descriptionInput}
                        onChange={(e) => setDescriptionInput(e.target.value)}
                        placeholder="Add details..."
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={2}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Priority
                      </label>
                      <select
                        value={priority}
                        onChange={(e) => setPriority(e.target.value as any)}
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value as any)}
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Assign To
                      </label>
                      <select
                        value={assignedTo || ''}
                        onChange={(e) => setAssignedTo(e.target.value ? Number(e.target.value) : null)}
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Unassigned</option>
                        {users.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.first_name} {user.last_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={selectedAnnotation ? handleUpdateAnnotation : handleSaveAnnotation}
                      disabled={!commentInput.trim()}
                      className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {selectedAnnotation ? 'Update' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedAnnotation(null);
                        setCommentInput('');
                        setDescriptionInput('');
                        setCurrentShape(null);
                        setDrawStart(null);
                        setPriority('medium');
                        setStatus('pending');
                        setAssignedTo(null);
                      }}
                      className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {currentFileAnnotations.length === 0 ? (
                  <div className="text-center py-8">
                    <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">
                      Select a tool and click on the image to add annotations
                    </p>
                  </div>
                ) : (
                  currentFileAnnotations.map((anno, idx) => (
                    <div
                      key={anno.id}
                      className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
                        selectedAnnotation === anno.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                      }`}
                      onClick={() => {
                        setSelectedAnnotation(anno.id);
                        setCommentInput(anno.title);
                        setDescriptionInput(anno.description || '');
                        setPriority(anno.priority);
                        setStatus(anno.status);
                        setAssignedTo(anno.assigned_to);
                        setCurrentShape(null);
                      }}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-800">#{idx + 1}</span>
                          {anno.annotation_type === 'point' && <MapPin className="w-3 h-3 text-gray-500" />}
                          {anno.annotation_type === 'rectangle' && <Square className="w-3 h-3 text-gray-500" />}
                          {anno.annotation_type === 'circle' && <Circle className="w-3 h-3 text-gray-500" />}
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getPriorityColor(anno.priority)}`}>
                            {anno.priority}
                          </span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteAnnotation(anno.id);
                          }}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <h4 className="font-medium text-gray-900 mb-1">{anno.title}</h4>

                      {anno.description && (
                        <p className="text-sm text-gray-600 mb-2">{anno.description}</p>
                      )}

                      <div className="text-xs text-gray-500 mb-2 font-mono">
                        {getAnnotationDescription(anno)}
                      </div>

                      <div className="flex items-center justify-between text-xs">
                        <span className={`px-2 py-1 rounded font-medium ${getStatusColor(anno.status)}`}>
                          {anno.status.replace('_', ' ')}
                        </span>
                        {anno.assigned_to && (
                          <span className="flex items-center gap-1 text-gray-600">
                            <User className="w-3 h-3" />
                            {anno.assignee_first_name} {anno.assignee_last_name}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Image Gallery View
  return (
    <div>
      {files.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Uploaded Images ({files.length})
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {files.map(file => {
              const fileAnnotations = annotations.filter(a => a.file_id === file.id);
              return (
                <div
                  key={file.id}
                  onClick={() => openAnnotator(file)}
                  className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg hover:border-blue-300 transition-all cursor-pointer group"
                >
                  <div className="aspect-video bg-gray-100 relative">
                    <img
                      src={file.file_url}
                      alt={file.original_name}
                      className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
                    />
                    <div className="absolute top-2 right-2 bg-blue-600 text-white px-2 py-1 rounded text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      Click to annotate
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium text-gray-900 mb-1 truncate">{file.original_name}</h3>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{(file.file_size / 1024).toFixed(2)} KB</span>
                      <span className="flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" />
                        {fileAnnotations.length} annotation{fileAnnotations.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(file.uploaded_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No images uploaded</h3>
          <p className="text-gray-500">Upload images to start creating annotations.</p>
        </div>
      )}
    </div>
  );
};
